// PRD 07 §3.2 — Content Settings section. Owns the add/edit/delete API for
// the block array. All three block types (Text / Image / Video) are wired
// here; saving with 0 blocks is allowed (PRD §4.2).

import { useTranslation } from 'react-i18next';

import { MAX_BLOCKS, canAddBlock } from '@/shared/utils/block-validation';

import type { ContentBlock } from '../types/management-guidelines.types';
import { ImageBlockEditor } from './ImageBlockEditor';
import { TextBlockEditor } from './TextBlockEditor';
import { VideoBlockEditor } from './VideoBlockEditor';

interface ContentBlockListProps {
  blocks: ContentBlock[];
  /** SHCT-370 — the block rules report ONE message for the whole section (§7.1 inline). */
  error?: string;
  onAddText: () => void;
  onAddImage: () => void;
  onAddVideo: () => void;
  onChangeBlock: (id: number, patch: Partial<ContentBlock>) => void;
  onDeleteBlock: (id: number) => void;
  /** SHCT-582 — PRD 21 §7.4 P-2: the whole section is inert for a METADATA:READ operator. */
  disabled?: boolean;
}

export function ContentBlockList({
  blocks,
  error,
  onAddText,
  onAddImage,
  onAddVideo,
  onChangeBlock,
  onDeleteBlock,
  disabled = false,
}: ContentBlockListProps) {
  const { t } = useTranslation('management-guidelines');
  // SHCT-370 — PRD 07 §3.2 line 126: a guideline holds at most MAX_BLOCKS blocks and reaching the cap must
  // BLOCK the add ("10개 도달 시 추가 차단"), with the message the spec names. Disabling the three buttons is
  // the visible half; GuidelineFormPanel's addBlock guards the state itself so a double-click cannot slip
  // past. Mirrors the counseling-manual and health-encyclopedia lists, which already do this.
  const atBlockLimit = !canAddBlock(blocks.length);

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onAddText}
          disabled={disabled || atBlockLimit}
        >
          T {t('blocks.addText')}
        </button>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onAddImage}
          disabled={disabled || atBlockLimit}
        >
          🖼 {t('blocks.addImage')}
        </button>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onAddVideo}
          disabled={disabled || atBlockLimit}
        >
          🎬 {t('blocks.addVideo')}
        </button>
        {atBlockLimit ? (
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            {t('blocks.limitReached', { max: MAX_BLOCKS })}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 360,
          overflow: 'auto',
          border: `1px solid ${error ? 'var(--destructive)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          padding: 8,
          background: '#fff',
        }}
      >
        {blocks.length === 0 ? (
          <div
            style={{
              padding: 16,
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-sm)',
              textAlign: 'center',
              color: 'var(--muted-foreground)',
              fontSize: 13,
            }}
          >
            {t('blocks.empty')}
          </div>
        ) : (
          blocks.map((block, index) => {
            const common = {
              key: block.id,
              block,
              index,
              onChange: (patch: Partial<ContentBlock>) => onChangeBlock(block.id, patch),
              onDelete: () => onDeleteBlock(block.id),
              disabled,
            };
            if (block.type === 'text') return <TextBlockEditor {...common} />;
            if (block.type === 'image') return <ImageBlockEditor {...common} />;
            return <VideoBlockEditor {...common} />;
          })
        )}
      </div>
      {error ? (
        <p className="form-field__error" style={{ marginTop: 4 }}>
          {error}
        </p>
      ) : null}
    </>
  );
}
